import { defaultImport, ErrorConstant, invariant, isElementDomNode } from '@remirror/core';
import _TurndownService from 'turndown';

const TurndownService = defaultImport(_TurndownService);

function getListNestLevel(node) {
  let level = 0;
  let parent = node.parentNode;
  while (parent) {
    if (parent.nodeName === 'UL' || parent.nodeName === 'OL') {
      level++;
    }
    parent = parent.parentNode;
  }
  return level;
}

function getParentListItemNumber(node) {
  let current = node.parentNode;

  // Traverse up the tree until we find an LI or OL/UL
  while (current && current.nodeName !== 'LI' && current.nodeName !== 'OL' && current.nodeName !== 'UL') {
    current = current.parentNode;
  }

  // If we find an LI, check its number
  if (current && current.nodeName === 'LI') {
    const olParent = current.closest('ol');
    if (olParent) {
      const previousSiblings = [...current.parentNode.children].slice(
        0,
        [...current.parentNode.children].indexOf(current)
      );
      const itemNumber =
        previousSiblings.filter((sibling) => sibling.nodeName === 'LI').length +
        (parseInt(olParent.getAttribute('start')) || 1);
      return itemNumber;
    }
  }

  return null;
}

export function htmlToMarkdown(html) {
  return turndownService.turndown(html);
}

function isHeadingRow(tableRow) {
  const parentNode = tableRow.parentNode;

  if (!isElementDomNode(parentNode)) {
    return false;
  }

  if (parentNode.nodeName === 'THEAD') {
    return true;
  }

  if (parentNode.nodeName !== 'TABLE' && !isFirstTbody(parentNode)) {
    return false;
  }

  const childNodes = [...tableRow.childNodes];

  // If the first row is all controller cells, consider the second row as the heading
  if (childNodes.every((n) => isControllerHeadingCell(n))) {
    const nextRow = tableRow.nextElementSibling;
    return nextRow && [...nextRow.childNodes].some((n) => n.nodeName === 'TD' && n.textContent.trim());
  }

  if (tableRow === tableRow.parentNode.rows[0] && childNodes.some((n) => n.textContent.trim())) {
    return true;
  }

  return childNodes.every((n) => n.nodeName === 'TH') && childNodes.some((n) => !!n.textContent);
}

/**
 * Controller cells are generated by the React Tables extension, and provide Node Views for adding/removing columns and rows
 *
 * However they should not be included in markdown output.
 */
function isControllerHeadingCell(cell) {
  return isElementDomNode(cell) && cell.matches('th[data-controller-cell]');
}

/**
 * A tableRow is a controller heading row if:
 * - the parent is a THEAD
 * - or if its the first child of the TABLE or the first TBODY (possibly
 *   following a blank THEAD)
 * - and every cell is a controller cell
 */
function isControllerHeadingRow(tableRow) {
  const parentNode = tableRow.parentNode;

  if (!isElementDomNode(parentNode)) {
    return false;
  }

  if (parentNode.nodeName !== 'TABLE' && !isFirstTbody(parentNode)) {
    return false;
  }

  const childNodes = [...tableRow.childNodes];
  return childNodes.every((n) => isControllerHeadingCell(n));
}

/**
 * Check whether this is the first `tbody` in the table.
 */
function isFirstTbody(element) {
  if (element.nodeName !== 'TBODY') {
    return false;
  }

  const previousSibling = element.previousSibling;

  if (!previousSibling) {
    return true;
  }

  return (
    isElementDomNode(previousSibling) && previousSibling.nodeName === 'THEAD' && !previousSibling.textContent?.trim()
  );
}

/**
 * Markdown does not support nested tables, check if current table has a table ancestor node
 */
function isNestedTable(element) {
  const currentTable = element.closest('table');

  if (!currentTable) {
    return false;
  }

  const { parentNode } = currentTable;

  if (!parentNode) {
    return true;
  }

  return !!parentNode.closest('table');
}

/**
 * Create a cell from the table.
 */
function cell(content, node) {
  const childNodes = [];

  for (const n of node.parentNode?.childNodes ?? []) {
    if (isControllerHeadingCell(n)) {
      continue;
    }

    childNodes.push(n);
  }

  const index = childNodes.indexOf(node);
  const prefix = index === 0 ? '| ' : ' ';

  return `${prefix + content.trim()} |`;
}

const turndownService = new TurndownService({
  codeBlockStyle: 'fenced',
  headingStyle: 'atx',
  blankReplacement: (content, node) => {
    const insideTable = node.closest('table') !== null;

    // If inside a table, ignore the blank replacement completely
    return insideTable ? '' : '<p></p>';
  },
})
  .addRule('tableCell', {
    filter: ['th', 'td'],
    replacement: (content, node) => {
      // Check if content is blank and return an empty string instead of <p> tag

      const trimmedContent = content.trim();
      if (!trimmedContent) {
        return 'ABCD';
      }

      // Otherwise, handle the normal table cell content
      return cell(trimmedContent, node);
    },
  })
  .addRule('iframe', {
    filter: 'iframe',
    replacement: (content, node) => {
      const src = node.getAttribute('src');
      const width = node.getAttribute('width') || '560';
      const height = node.getAttribute('height') || '315';

      // For other iframes, preserve the HTML
      return `<iframe src="${src}" width="${width}" height="${height}" frameborder="0" allowfullscreen></iframe>`;
    },
  })
  .addRule('list', {
    filter: ['ul', 'ol'],
    replacement: (content, node) => {
      const isOrdered = node.nodeName === 'OL';
      const start = node.getAttribute('start');
      let markdown = content.trim();
      // Adjust start number for ordered lists
      if (isOrdered && start) {
        markdown = markdown.replace(/^1\./gm, `${start}.`);
      }

      const parentListItemNumber = getParentListItemNumber(node);
      // Adjust indentation for nested lists
      let indent = '';
      const nestLevel = getListNestLevel(node);
      if (nestLevel === 1 && parentListItemNumber >= 10) indent = ' '.repeat(4);
      markdown = markdown.replace(/^/gm, indent);
      return `\n\n${markdown}\n\n`;
    },
  })
  .addRule('taskListItems', {
    filter: (node) => node.nodeName === 'LI' && node.hasAttribute('data-task-list-item'),
    replacement: (content, node) => {
      const isChecked = node.hasAttribute('data-checked');
      const checkboxMarkdown = `- ${isChecked ? '[x]' : '[ ]'} `;

      // If the content contains nested elements, ensure correct indentation
      content = content.replace(/\n/g, '\n    '); // Indent nested list items by 4 spaces for Markdown
      return (
        checkboxMarkdown + content.trimStart() + (node.nextSibling && node.nextSibling.nodeName === 'LI' ? '\n' : '')
      );
    },
  })
  .addRule('tableCell', {
    filter: ['th', 'td'],
    replacement: (content, node) => {
      if (isControllerHeadingCell(node)) {
        return '';
      }

      return cell(content, node);
    },
  })
  .addRule('tableRow', {
    filter: 'tr',
    replacement: (content, node) => {
      let borderCells = '';
      const alignMap = { left: ':--', right: '--:', center: ':-:' };

      // Get child nodes ignoring controller cells
      const childNodes = [...node.childNodes].filter((n) => !isControllerHeadingCell(n));

      if (isHeadingRow(node) || node === node.parentNode.rows[1]) {
        for (const childNode of childNodes) {
          if (!isElementDomNode(childNode)) {
            continue;
          }

          let border = '---';
          const align = (childNode.getAttribute('align') ?? '').toLowerCase();

          if (align) {
            border = alignMap[align] || border;
          }

          borderCells += cell(border, childNode);
        }
      }

      return `\n${content}${borderCells ? `\n${borderCells}` : ''}`;
    },
  })
  .addRule('table', {
    filter: (node) => {
      if (node.nodeName !== 'TABLE') {
        return false;
      }

      if (isNestedTable(node)) {
        return false;
      }

      const rows = [...node.rows].filter((r) => !isControllerHeadingRow(r));
      // Remove the condition that requires more than one row
      return rows.length > 0;
    },
    replacement: (content) => {
      content = content.replace('\n\n', '\n');
      return `\n\n${content}\n\n`;
    },
  })
  .addRule('tableSection', {
    filter: ['thead', 'tbody', 'tfoot'],
    replacement: function (content) {
      return content;
    },
  })
  .keep((node) => node.nodeName === 'TABLE' && !isHeadingRow(node))
  .keep((node) => node.nodeName === 'TABLE' && isNestedTable(node))
  .addRule('strikethrough', {
    filter: ['del', 's', 'strike'],
    replacement: function (content) {
      return `~${content}~`;
    },
  })
  .addRule('fencedCodeBlock', {
    filter: (node, options) =>
      !!(
        options.codeBlockStyle === 'fenced' &&
        node.nodeName === 'PRE' &&
        node.firstChild &&
        node.firstChild.nodeName === 'CODE'
      ),

    replacement: (_, node, options) => {
      invariant(isElementDomNode(node.firstChild), {
        code: ErrorConstant.EXTENSION,
        message: `Invalid node \`${node.firstChild?.nodeName}\` encountered for codeblock when converting html to markdown.`,
      });

      const className = node.firstChild.getAttribute('class') ?? '';
      const language =
        className.match(/(?:lang|language)-(\S+)/)?.[1] ??
        node.firstChild.getAttribute('data-code-block-language') ??
        '';

      return `\n\n${options.fence}${language}\n${node.firstChild.textContent}\n${options.fence}\n\n`;
    },
  });

export default htmlToMarkdown;
